import {
  Input,
  InputProps,
  makeStyles,
  mergeClasses,
} from "@fluentui/react-components";
import { useEffect, useState } from "react";
import { $cloud40 } from "../../../../../cssjs/colors";
import { ITimeState } from "../../../../../models/common/DateTimeModels";

const getHourValue = (hour: number, padHour: boolean) => {
  const isNegative = hour < 0 || Object.is(hour, -0);
  const absoluteValue = Math.abs(hour).toString();
  const displayValue = padHour ? absoluteValue.padStart(2, "0") : absoluteValue;

  return isNegative ? `-${displayValue}` : displayValue;
};

const getMinuteValue = (minute: number, padMinute: boolean) => {
  const value = minute.toString();
  return padMinute ? value.padStart(2, "0") : value;
};

interface ITimePickerProps {
  rollingtime: ITimeState;
  className?: string;
  allowNegative?: boolean;
  padHour?: boolean;
  padMinute?: boolean;
}

const TimePicker: React.FC<ITimePickerProps> = ({
  className = "",
  rollingtime,
  allowNegative = true,
  padHour = false,
  padMinute = false,
}) => {
  const styles = useStyles();
  const minLimit = allowNegative ? -23 : 0;

  const [hour, setHour] = useState<string>(
    getHourValue(rollingtime.hour, padHour)
  );
  const [minute, setMinute] = useState<string>(
    getMinuteValue(rollingtime.minute, padMinute)
  );

  useEffect(() => {
    setHour(getHourValue(rollingtime.hour, padHour));
    setMinute(getMinuteValue(rollingtime.minute, padMinute));
  }, [rollingtime.hour, rollingtime.minute, padHour, padMinute]);

  const onHourChangeHandler: InputProps["onChange"] = (_ev, data) => {
    const inputValue = data.value;
    // Allow empty input and negative sign at the start
    if (inputValue === "" || (allowNegative && inputValue === "-")) {
      setHour(inputValue);
      return;
    }

    const numberValue = Number(inputValue);
    if (
      !isNaN(numberValue) &&
      numberValue >= minLimit &&
      numberValue <= 23 &&
      inputValue.length <= 3
    ) {
      setHour(inputValue);
    }
  };

  const onMinuteChangeHandler: InputProps["onChange"] = (_ev, data) => {
    const inputValue = data.value;
    // Allow empty input and negative sign at the start
    if (inputValue === "") {
      setMinute(inputValue);
      return;
    }

    const numberValue = Number(inputValue); // Check if the input is a valid number within the range
    if (
      !isNaN(numberValue) &&
      numberValue >= 0 &&
      numberValue <= 59 &&
      inputValue.length <= 2
    ) {
      setMinute(inputValue);
    }
  };

  const onHourBlurHandler: React.FocusEventHandler<HTMLInputElement> = (ev) => {
    const inputHrValue = ev.target.value;
    const numberValue = Number(inputHrValue);

    const validHour =
      inputHrValue !== "" && !isNaN(numberValue) ? numberValue : 0;

    setHour(getHourValue(validHour, padHour));
    rollingtime.setHour(validHour);
  };

  const onMinuteBlurHandler: React.FocusEventHandler<HTMLInputElement> = (
    ev
  ) => {
    const inputMinValue = ev.target.value;
    const numberValue = Number(inputMinValue);

    const validMinute =
      inputMinValue !== "" && !isNaN(numberValue) ? numberValue : 0;

    setMinute(getMinuteValue(validMinute, padMinute));
    rollingtime.setMinute(validMinute);
  };

  return (
    <div
      className={mergeClasses(className, styles.container)}
      data-testid="time-picker-input"
    >
      <Input
        type="text"
        value={hour}
        onChange={onHourChangeHandler}
        onBlur={onHourBlurHandler}
        className={styles.customInput}
      />
      <span>:</span>
      <Input
        type="text"
        value={minute}
        onChange={onMinuteChangeHandler}
        onBlur={onMinuteBlurHandler}
        className={styles.customInput}
      />
    </div>
  );
};
export default TimePicker;

const useStyles = makeStyles({
  customInput: {
    width: "45px",
    columnGap: "0px",
    border: "none",
    "& input": {
      textAlign: "center",
      padding: "0px",
    },
  },
  container: { borderRadius: "5px", border: `1px solid ${$cloud40}` },
});
